import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Forward, Loader2, MessageSquareReply, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { type AttachmentRow } from '@/api/attachments';
import { analyticsApi, type ReplyThread, type ThreadBubble } from '@/api/misc';
import { AttachmentPicker } from '@/components/attachment-picker';
import { RichTextEditor } from '@/components/rich-text-editor';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { StatusPill } from '@/components/ui/status-pill';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { sanitizeEmailHtml } from '@/lib/sanitize-email-html';
import { qk } from '@/lib/query-keys';

interface ReplyItem {
  event_id: string;
  created_at: string;
  lead_id: string;
  lead_email: string;
  lead_name: string | null;
  company: string | null;
  status: string;
  payload: {
    from?: string;
    subject?: string;
    intent?: string;
    body_preview?: string;
    engine?: 'deepseek' | 'mlm';
  };
}

export function RepliesPage(): JSX.Element {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: qk.analytics.replies,
    queryFn: () =>
      analyticsApi
        .replies()
        .then((r) => (r.items as unknown[]).map((x) => x as unknown as ReplyItem)),
  });

  const items = list.data ?? [];

  return (
    <>
      <Topbar title="Inbox" description={`${items.length} thread${items.length === 1 ? '' : 's'}`} />
      <div className="flex-1 overflow-auto p-6">
        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={MessageSquareReply}
            title="No replies yet"
            description="When your campaigns get replies, they'll appear here."
          />
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <button
                key={r.event_id}
                type="button"
                onClick={() => setOpenThreadId(r.event_id)}
                className="mb-card w-full text-left p-4 transition-colors hover:border-mb-accent focus:outline-none focus:ring-2 focus:ring-mb-accent"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-mb-text truncate">
                      {r.lead_name ?? r.lead_email}
                      {r.company ? (
                        <span className="text-mb-text-muted"> · {r.company}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-mb-text-muted mt-0.5 truncate">
                      {r.payload.subject ?? '(no subject)'}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusPill status={r.status} />
                    {r.payload.intent ? (
                      <span className="text-[10px] text-mb-text-subtle">
                        intent: {r.payload.intent}
                      </span>
                    ) : null}
                  </div>
                </div>
                {r.payload.body_preview ? (
                  <div className="mt-2 text-sm text-mb-text-muted line-clamp-2">
                    {r.payload.body_preview}
                  </div>
                ) : null}
                <div className="mt-2 text-[10px] text-mb-text-subtle flex items-center gap-2">
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  {r.payload.engine ? (
                    <span className="rounded bg-mb-bg-soft px-1.5 py-0.5 uppercase">
                      {r.payload.engine}
                    </span>
                  ) : null}
                  <span className="ml-auto text-mb-accent-press">Open thread →</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <ThreadModal
        eventId={openThreadId}
        onClose={() => setOpenThreadId(null)}
      />
    </>
  );
}

/**
 * Chat-style modal: header w/ lead info, scrollable bubble list, sticky
 * composer at the bottom. Outbound bubbles align right, inbound left.
 */
function ThreadModal({
  eventId,
  onClose,
}: {
  eventId: string | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const thread = useQuery({
    queryKey: ['reply-thread', eventId],
    queryFn: () => analyticsApi.replyThread(eventId as string),
    enabled: Boolean(eventId),
    staleTime: 10_000,
  });

  const [mode, setMode] = useState<'reply' | 'forward'>('reply');
  const [draftHtml, setDraftHtml] = useState('');
  const [forwardTo, setForwardTo] = useState('');
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: async () => {
      const ids = attachments.map((a) => a.id);
      if (mode === 'reply') {
        return analyticsApi.sendReply(eventId as string, {
          body_html: draftHtml,
          attachment_ids: ids,
        });
      }
      return analyticsApi.sendForward(eventId as string, {
        to: forwardTo.trim(),
        body_html: buildForwardBody(draftHtml, thread.data),
        attachment_ids: ids,
      });
    },
    onSuccess: async () => {
      setDraftHtml('');
      setForwardTo('');
      setAttachments([]);
      setMode('reply');
      setSendError(null);
      await thread.refetch();
      await qc.invalidateQueries({ queryKey: qk.analytics.replies });
    },
    onError: (err) => {
      setSendError(err instanceof ApiError ? err.message : 'Failed to send.');
    },
  });

  // Auto-scroll to bottom whenever the message list changes.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread.data?.messages.length]);

  // Reset local state when the modal closes/opens a different thread.
  useEffect(() => {
    if (!eventId) {
      setDraftHtml('');
      setForwardTo('');
      setAttachments([]);
      setMode('reply');
      setSendError(null);
    }
  }, [eventId]);

  const open = Boolean(eventId);
  const data = thread.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={data?.subject ?? 'Thread'}
      size="lg"
    >
      {thread.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : thread.isError || !data ? (
        <ErrorState onRetry={() => void thread.refetch()} />
      ) : (
        <div className="flex flex-col" style={{ height: '70vh' }}>
          <ThreadHeader data={data} />

          {/* Scrollable bubble stream */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-2 py-3 space-y-3 bg-mb-bg-soft rounded-md border border-mb-border"
          >
            {data.messages.length === 0 ? (
              <div className="text-center text-xs text-mb-text-muted py-6">
                No messages in this thread yet.
              </div>
            ) : (
              data.messages.map((m, i) => <Bubble key={i} m={m} />)
            )}
          </div>

          {/* Composer */}
          <Composer
            canSend={data.compose.can_send}
            fromEmail={data.compose.mailbox_email}
            toEmail={data.compose.to}
            mode={mode}
            setMode={setMode}
            draftHtml={draftHtml}
            setDraftHtml={setDraftHtml}
            forwardTo={forwardTo}
            setForwardTo={setForwardTo}
            attachments={attachments}
            setAttachments={setAttachments}
            pending={send.isPending}
            onSend={() => {
              const empty = !stripTags(draftHtml).trim();
              if (empty) return;
              if (mode === 'forward' && !forwardTo.trim()) {
                setSendError('Enter a recipient email to forward to.');
                return;
              }
              send.mutate();
            }}
            error={sendError}
          />
        </div>
      )}
    </Modal>
  );
}

function ThreadHeader({ data }: { data: ReplyThread }): JSX.Element {
  return (
    <div className="rounded-md border border-mb-border bg-mb-surface px-3 py-2 mb-3 text-xs flex items-center gap-3 flex-wrap">
      <span className="font-medium text-mb-text">{data.lead.name ?? data.lead.email}</span>
      {data.lead.company ? (
        <span className="text-mb-text-muted">{data.lead.company}</span>
      ) : null}
      <span className="text-mb-text-subtle font-mono">{data.lead.email}</span>
      <StatusPill status={data.lead.status} />
      {data.compose.mailbox_email ? (
        <span className="ml-auto text-[10px] text-mb-text-subtle font-mono">
          via {data.compose.mailbox_email}
        </span>
      ) : null}
    </div>
  );
}

function Bubble({ m }: { m: ThreadBubble }): JSX.Element {
  const outbound = m.direction === 'outbound';
  const bubbleClasses = outbound
    ? 'ml-auto bg-mb-accent text-mb-accent-fg'
    : 'mr-auto bg-white border border-mb-border text-mb-text';
  const align = outbound ? 'items-end' : 'items-start';

  return (
    <div className={`flex flex-col ${align} max-w-[85%] ${outbound ? 'ml-auto' : 'mr-auto'}`}>
      {/* meta line (from/at) */}
      <div className="text-[10px] text-mb-text-subtle mb-1 flex items-center gap-2">
        {m.from ? <span className="font-mono">{m.from_name ?? m.from}</span> : null}
        <span>{m.at ? new Date(m.at).toLocaleString() : ''}</span>
        {m.kind === 'sent' ? (
          <span className="rounded bg-mb-bg-soft px-1 py-0.5 uppercase text-mb-text-muted">
            campaign
          </span>
        ) : null}
        {m.kind === 'manual_reply' ? (
          <span className="rounded bg-mb-bg-soft px-1 py-0.5 uppercase text-mb-text-muted">
            reply
          </span>
        ) : null}
        {m.kind === 'manual_forward' ? (
          <span className="rounded bg-mb-bg-soft px-1 py-0.5 uppercase text-mb-text-muted">
            forwarded
          </span>
        ) : null}
        {m.intent ? (
          <span className="rounded bg-mb-accent-soft px-1 py-0.5 text-mb-accent-press uppercase">
            {m.intent}
          </span>
        ) : null}
        {m.engine ? (
          <span className="rounded bg-mb-bg-soft px-1 py-0.5 uppercase">
            {m.engine}
          </span>
        ) : null}
      </div>

      {/* bubble */}
      <div className={`rounded-2xl px-4 py-2.5 shadow-sm ${bubbleClasses}`}>
        {m.subject ? (
          <div className={`font-medium text-sm mb-1 ${outbound ? 'text-mb-accent-fg' : 'text-mb-text'}`}>
            {m.subject}
          </div>
        ) : null}
        {m.body_html ? (
          <div
            className={`prose prose-sm max-w-none ${outbound ? 'prose-invert' : ''}`}
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(m.body_html) }}
          />
        ) : m.body ? (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm">
            {m.body}
          </pre>
        ) : (
          <div className="text-xs italic opacity-70">(empty body)</div>
        )}
      </div>
    </div>
  );
}

function Composer({
  canSend,
  fromEmail,
  toEmail,
  mode,
  setMode,
  draftHtml,
  setDraftHtml,
  forwardTo,
  setForwardTo,
  attachments,
  setAttachments,
  pending,
  onSend,
  error,
}: {
  canSend: boolean;
  fromEmail: string | null;
  toEmail: string;
  mode: 'reply' | 'forward';
  setMode: (m: 'reply' | 'forward') => void;
  draftHtml: string;
  setDraftHtml: (v: string) => void;
  forwardTo: string;
  setForwardTo: (v: string) => void;
  attachments: AttachmentRow[];
  setAttachments: (a: AttachmentRow[]) => void;
  pending: boolean;
  onSend: () => void;
  error: string | null;
}): JSX.Element {
  if (!canSend) {
    return (
      <div className="mt-3 rounded-md border border-dashed border-mb-border p-3 text-xs text-mb-text-muted italic">
        Can't reply or forward here — this thread isn't matched to a specific
        outbound campaign message, so there's no mailbox to send from.
      </div>
    );
  }

  const empty = !stripTags(draftHtml).trim();

  return (
    <div className="mt-3 rounded-md border border-mb-border bg-mb-surface p-3">
      {/* Reply / Forward tabs */}
      <div className="mb-2 flex gap-1 border-b border-mb-border">
        <TabBtn active={mode === 'reply'} onClick={() => setMode('reply')} icon={Send}>
          Reply
        </TabBtn>
        <TabBtn active={mode === 'forward'} onClick={() => setMode('forward')} icon={Forward}>
          Forward
        </TabBtn>
      </div>

      {/* From / To strip */}
      <div className="text-[10px] text-mb-text-subtle mb-1.5">
        from <b className="font-mono">{fromEmail}</b>
        {mode === 'reply' ? (
          <>
            {' '}· to <b className="font-mono">{toEmail}</b>
          </>
        ) : null}
      </div>

      {/* Forward-only recipient input */}
      {mode === 'forward' ? (
        <div className="mb-2">
          <label className="block text-[10px] uppercase tracking-wider text-mb-text-subtle mb-0.5">
            Forward to
          </label>
          <input
            type="email"
            value={forwardTo}
            onChange={(e) => setForwardTo(e.target.value)}
            placeholder="person@example.com"
            className="mb-input text-sm"
            disabled={pending}
          />
        </div>
      ) : null}

      {/* Editor */}
      <div
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !empty && !pending) {
            e.preventDefault();
            onSend();
          }
        }}
      >
        <RichTextEditor
          value={draftHtml}
          onChange={setDraftHtml}
          placeholder={
            mode === 'reply'
              ? 'Type your reply… (⌘+Enter to send)'
              : 'Add a note above the forwarded thread… (⌘+Enter to send)'
          }
          rows={4}
          disabled={pending}
        />
      </div>

      {/* Attachments */}
      <div className="mt-2">
        <AttachmentPicker
          attached={attachments}
          onChange={setAttachments}
          disabled={pending}
        />
      </div>

      {error ? (
        <div role="alert" className="mt-2 rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
          {error}
        </div>
      ) : null}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          className="mb-btn-primary"
          disabled={pending || empty || (mode === 'forward' && !forwardTo.trim())}
          onClick={onSend}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : mode === 'reply' ? (
            <Send className="h-4 w-4" />
          ) : (
            <Forward className="h-4 w-4" />
          )}
          {mode === 'reply' ? 'Send reply' : 'Send forward'}
        </button>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon: typeof Send;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs transition-colors ' +
        (active
          ? 'border-mb-accent text-mb-accent-press font-medium'
          : 'border-transparent text-mb-text-muted hover:text-mb-text')
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

/** Quick text-only view of an HTML string — for "is the composer empty?"
 *  detection. Not for security. */
function stripTags(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ');
}

/**
 * Prepend a "Forwarded message" quote block to whatever the user typed,
 * so the recipient sees the note plus the original thread beneath.
 */
function buildForwardBody(userHtml: string, thread: ReplyThread | undefined): string {
  if (!thread) return userHtml;
  const blocks: string[] = [];
  for (const m of thread.messages) {
    const bodyHtml = m.body_html || (m.body ? `<pre>${escapeHtml(m.body)}</pre>` : '');
    if (!bodyHtml) continue;
    const header = [
      m.from ? `<b>From:</b> ${escapeHtml(m.from_name ? `${m.from_name} <${m.from}>` : m.from)}` : null,
      m.to ? `<b>To:</b> ${escapeHtml(m.to)}` : null,
      m.at ? `<b>Date:</b> ${new Date(m.at).toLocaleString()}` : null,
      m.subject ? `<b>Subject:</b> ${escapeHtml(m.subject)}` : null,
    ]
      .filter(Boolean)
      .join('<br>');
    blocks.push(
      `<div style="margin:8px 0;padding:8px 12px;border-left:2px solid #ccc;">
         <div style="color:#666;font-size:12px;">${header}</div>
         <div style="margin-top:6px;">${bodyHtml}</div>
       </div>`,
    );
  }
  const quoted = blocks.length
    ? `<hr style="border:none;border-top:1px solid #eee;margin:12px 0" />
       <div style="color:#666;font-size:12px;"><i>---------- Forwarded message ----------</i></div>
       ${blocks.join('')}`
    : '';
  return `${userHtml}\n${quoted}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
